import JSONbig from "json-bigint";
import { RequestResultValue } from "../../../common/api/requestResult";
import { HttpService } from "../../../common/api/httpService";
import { OrderFailReason } from "../../../models/order";
import {
    OrderFailDetailsReport,
    OrderFailReasonsReport,
    OrderSummaryReport,
    OrderTimingReport,
    StatsGranularity,
} from "../../../models/orderAnalytics";

const JSONbigNative = JSONbig({ useNativeBigInt: true });

export type OrdersAnalyticsPeriodParams = {
    from: Date;
    to: Date;
    gameId?: number;
};

async function getJson<T>(path: string): Promise<RequestResultValue<T>> {
    const response = await HttpService.getInstance().requestGetAsync(path);
    const text = await response.text();
    if (response.status < 200 || response.status >= 300) {
        return new RequestResultValue<T>(false);
    }
    // json-bigint + useNativeBigInt падает на дробных процентах — тогда обычный JSON
    try {
        return new RequestResultValue<T>(true, JSONbigNative.parse(text) as T);
    } catch {
        return new RequestResultValue<T>(true, JSON.parse(text) as T);
    }
}

function periodQuery(params: OrdersAnalyticsPeriodParams): URLSearchParams {
    const q = new URLSearchParams();
    q.set("from", params.from.toISOString());
    q.set("to", params.to.toISOString());
    if (params.gameId) {
        q.set("gameId", String(params.gameId));
    }
    return q;
}

/** Итог по группам статусов, разрез по играм и ряд по времени; период [from; to) не больше 366 дней */
export async function ordersSummaryApi(
    params: OrdersAnalyticsPeriodParams & { granularity: StatsGranularity }
): Promise<RequestResultValue<OrderSummaryReport>> {
    const q = periodQuery(params);
    q.set("granularity", String(params.granularity));
    return getJson<OrderSummaryReport>(`admin/orders/analytics/summary?${q.toString()}`);
}

/** Причины неуспеха (Failed + Refunded), виновники, причина × игра и ряд топ-5 причин */
export async function ordersFailReasonsApi(
    params: OrdersAnalyticsPeriodParams & { granularity: StatsGranularity }
): Promise<RequestResultValue<OrderFailReasonsReport>> {
    const q = periodQuery(params);
    q.set("granularity", String(params.granularity));
    return getJson<OrderFailReasonsReport>(`admin/orders/analytics/fail-reasons?${q.toString()}`);
}

/** Неуспешные по сигнатуре текста ошибки и коды Steam; без failReason — все причины (топ-200 текстов на запрос) */
export async function ordersFailDetailsApi(
    params: OrdersAnalyticsPeriodParams & { failReason?: OrderFailReason }
): Promise<RequestResultValue<OrderFailDetailsReport>> {
    const q = periodQuery(params);
    if (params.failReason !== undefined) {
        q.set("failReason", String(params.failReason));
    }
    return getJson<OrderFailDetailsReport>(`admin/orders/analytics/fail-details?${q.toString()}`);
}

/** Скорость исполнения по этапам и зависшие заказы периода старше stuckHours (1–8760) */
export async function ordersTimingApi(
    params: OrdersAnalyticsPeriodParams & { stuckHours: number }
): Promise<RequestResultValue<OrderTimingReport>> {
    const q = periodQuery(params);
    q.set("stuckHours", String(params.stuckHours));
    return getJson<OrderTimingReport>(`admin/orders/analytics/timing?${q.toString()}`);
}
